import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { Navigate, RouterDataResolved } from "@ngxs/router-plugin";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { catchError, tap } from "rxjs/operators";
import { IPortal } from "src/app/model/custom/IPortal";
import { JsonDataService } from "src/app/shared/service/json-data.service";
import { ThemeService } from "src/app/shared/service/theme.service";
import { StoredAuthState } from "src/app/shared/state/stored-auth.state";
import { RouterStateParams } from "../../../shared/service/router-serializer";
import { PortalService } from "../service/portal.service";
import { GetPortalAction, PortalNotFoundAction } from "./portal.actions";

export class PortalStateModel {
	public isFinishedLoading: boolean;
	public portal: IPortal;
	public seoName: string;
}

@State<PortalStateModel>({
	name: "portal",
	defaults: {
		isFinishedLoading: true,
		portal: null,
		seoName: null,
	},
})
@Injectable()
export class PortalState {
	private portalService = inject(PortalService);
	private themeService = inject(ThemeService);
	private router = inject(Router);

	/** Inserted by Angular inject() migration for backwards compatibility */
	constructor(...args: unknown[]);

	constructor() {}

	@Selector()
	static isLoading(state: PortalStateModel) {
		return state.isFinishedLoading;
	}

	@Selector()
	static portal(state: PortalStateModel) {
		return state.portal;
	}

	@Selector()
	static portalTextForEmail(state: PortalStateModel) {
		return state?.portal?.SiteSettings?.PortalTextForEmail || "Your Email";
	}
	@Selector()
	static seoName(state: PortalStateModel) {
		return state.seoName;
	}

	@Selector()
	static oauthOnlyLogin(state: PortalStateModel) {
		return !!state.portal?.SiteSettings?.SSOOnlyLogin;
	}
	@Selector()
	static oauthDomain(state: PortalStateModel) {
		return state.portal?.OauthDomain;
	}
	@Selector([
		PortalState.oauthOnlyLogin,
		PortalState.oauthDomain,
		StoredAuthState.userDomain,
	])
	static isSSOUserDomainMatch(
		oauthOnlyLogin: boolean,
		oauthDomain: string,
		userDomain: string,
	) {
		if (!oauthOnlyLogin || !userDomain || !oauthDomain) return true;
		return oauthDomain === userDomain;
	}

	/************************************* */

	@Action(RouterDataResolved)
	RouterDataResolved(
		ctx: StateContext<PortalStateModel>,
		action: RouterDataResolved<RouterStateParams>,
	) {
		const { params, url } = action?.routerState;
		if (
			!url ||
			url.includes("/not-found") ||
			(!url.startsWith("/portal/") &&
				!url.startsWith("/contractor/") &&
				!url.startsWith("/sign-in/"))
		) {
			return;
		}
		ctx.patchState({ seoName: params.seoName });
		return ctx.dispatch([new GetPortalAction(params.seoName)]);
	}

	@Action(GetPortalAction)
	GetPortalAction(ctx: StateContext<PortalStateModel>, action: GetPortalAction) {
		return this.portalService.getPortalDetails(action.payload).pipe(
			tap((portal: IPortal) => {
				portal.Settings = JsonDataService.parse(portal.Settings);
				portal.CompanySuppliedDocumentsObj = JsonDataService.parse(
					portal.CompanySuppliedDocumentsObj,
				);
				ctx.patchState({
					portal: this.themeService.applyToPortal(portal),
				});
			}),
			catchError((error) => {
				// console.log(this.router.url);
				return ctx.dispatch([new PortalNotFoundAction()]);
			}),
		);
	}

	@Action(PortalNotFoundAction)
	PortalNotFoundAction(
		ctx: StateContext<PortalStateModel>,
		action: PortalNotFoundAction,
	) {
		return ctx.dispatch([new Navigate([`${this.router.url}/not-found`])]);
	}
}
